import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../lib/logger';
import { recipesAPI } from '../lib/api';
import { Navbar } from '../components/layout/Navbar';
import { RecipeCard } from '../components/recipes/RecipeCard';
import { Button } from '../components/ui/button';
import {
  User, Mail, Calendar, ChefHat, Heart, LogOut, Plus, Utensils
} from 'lucide-react';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [myRecipes, setMyRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchMyRecipes = async () => {
      try {
        const response = await recipesAPI.getMyRecipes();
        setMyRecipes(response.data);
      } catch (error) {
        logger.error('Failed to fetch recipes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMyRecipes();
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  const createdAt = new Date(user.created_at).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-worldplate-cream">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl p-8 md:p-12 border border-stone-100 shadow-sm mb-12"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            {/* Avatar */}
            <div className="w-24 h-24 md:w-32 md:h-32 bg-worldplate-secondary rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-12 h-12 md:w-16 md:h-16 text-white" />
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-worldplate-deep mb-2">
                {user.username}
              </h1>
              <div className="flex flex-wrap gap-4 text-worldplate-muted">
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Membre depuis {createdAt}
                </span>
              </div>
              
              {/* Stats */}
              <div className="flex gap-6 mt-6">
                <div className="text-center">
                  <p className="font-heading text-2xl font-bold text-worldplate-primary">
                    {myRecipes.length}
                  </p>
                  <p className="text-sm text-worldplate-muted">Recettes</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 w-full md:w-auto">
              <Link to="/recipes/new">
                <Button className="w-full bg-worldplate-primary hover:bg-worldplate-primary/90 text-white rounded-full gap-2" data-testid="add-recipe-btn">
                  <Plus className="w-5 h-5" />
                  Nouvelle recette
                </Button>
              </Link>
              <Link to="/favorites">
                <Button variant="outline" className="w-full rounded-full gap-2" data-testid="my-favorites-btn">
                  <Heart className="w-5 h-5" />
                  Mes favoris
                </Button>
              </Link>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full text-worldplate-error hover:text-worldplate-error/80 hover:bg-red-50 rounded-full gap-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                Déconnexion
              </Button>
            </div>
          </div>
        </motion.div>

        {/* My Recipes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <ChefHat className="w-7 h-7 text-worldplate-primary" />
              <h2 className="font-heading text-2xl font-bold text-worldplate-deep">
                Mes recettes
              </h2>
            </div>
            <Link to="/recipes/new">
              <Button variant="ghost" className="text-worldplate-primary hover:text-worldplate-primary/80 gap-2" data-testid="add-recipe-link">
                <Plus className="w-4 h-4" />
                Ajouter
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : myRecipes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {myRecipes.map((recipe, index) => (
                <RecipeCard key={recipe.id} recipe={recipe} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
              <div className="w-16 h-16 bg-worldplate-subtle rounded-full flex items-center justify-center mx-auto mb-6">
                <Utensils className="w-8 h-8 text-worldplate-muted" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-worldplate-deep mb-2">
                Aucune recette publiée
              </h3>
              <p className="text-worldplate-muted mb-6">
                Partagez vos créations culinaires avec la communauté !
              </p>
              <Link to="/recipes/new">
                <Button className="bg-worldplate-primary hover:bg-worldplate-primary/90 text-white rounded-full px-6" data-testid="create-first-recipe-btn">
                  Créer ma première recette
                </Button>
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
